/**
 * FinancialNoData — the domain adapter over ui/NoDataState. Owns the financial
 * copy so the wording stays out of the cross-cutting primitive.
 *
 * Each state states what is ACTUALLY true rather than a blanket "no data
 * available". That distinction matters: a company can have ten years of Profit &
 * Loss and still be unanalysable because the Balance Sheet is missing — telling
 * that user "no data available" contradicts the filings they can plainly see,
 * and hides the one fact they need (which statement is missing, and what to do).
 *
 * So each message answers three things: what we have, what's missing, and the
 * next action.
 */
import { Database, TrendingUp, Sparkles } from 'lucide-react';
import NoDataState from '../ui/NoDataState';

export type FinancialDataState = 'not-ingested' | 'no-records' | 'metric-missing' | 'incomplete';

export interface FinancialNoDataProps {
  state: FinancialDataState;
  companyName?: string;
  country?: string;
  /** Metric name for the `metric-missing` state, e.g. "CAP" or "EV / EBITDA". */
  metricLabel?: string;
  /** Which required statement is absent — drives the `incomplete` state's hint. */
  missing?: 'balance-sheet' | 'profit-loss' | 'both';
  /** Backend `detail` string, shown muted. */
  detail?: string;
  /** Wires the "Run research" action on the not-ingested state. */
  onResearch?: () => void;
  cardTitle?: string;
  cardSubtitle?: string;
  asCard?: boolean;
  minHeight?: number;
}

export default function FinancialNoData({
  state,
  companyName,
  country,
  metricLabel,
  missing,
  detail,
  onResearch,
  cardTitle,
  cardSubtitle,
  asCard = true,
  minHeight,
}: FinancialNoDataProps) {
  const who = companyName ?? 'This company';

  if (state === 'incomplete') {
    // The company DOES have data — it just can't be analysed. Name the missing
    // statement, say what it blocks, and give the next step.
    const title =
      missing === 'both'
        ? 'Financial statements unavailable'
        : missing === 'balance-sheet'
        ? 'Balance Sheet unavailable'
        : 'Profit & Loss unavailable';
    const hint =
      missing === 'both'
        ? `Neither the Balance Sheet nor the Profit & Loss could be read for ${who}'s latest reporting year. Both are needed to calculate enterprise value, ROIC, WACC and the valuation metrics.`
        : missing === 'balance-sheet'
        ? `${who} has Profit & Loss figures, but no Balance Sheet for the latest reporting year. The Balance Sheet supplies invested capital and net debt, so enterprise value, ROIC and WACC can't be calculated without it.`
        : `${who} has Balance Sheet figures, but no Profit & Loss for the latest reporting year. Revenue, EBITDA and earnings come from the P&L, so the valuation metrics can't be calculated without it.`;
    return (
      <NoDataState
        title={title}
        hint={hint}
        message="Re-running financial research may pick up the missing statement if the source now publishes it."
        icon={Database}
        variant="neutral"
        action={onResearch ? { label: 'Re-run financial research', onClick: onResearch, icon: Sparkles } : undefined}
        asCard={asCard}
        cardTitle={cardTitle}
        cardSubtitle={cardSubtitle}
        minHeight={minHeight}
      />
    );
  }

  if (state === 'metric-missing') {
    return (
      <NoDataState
        title={`${metricLabel ?? 'This metric'} unavailable`}
        hint={`${metricLabel ?? 'This metric'} was not reported, or could not be derived, for the years selected. Try widening the year range.`}
        icon={TrendingUp}
        variant="neutral"
        asCard={asCard}
        cardTitle={cardTitle}
        cardSubtitle={cardSubtitle}
        minHeight={minHeight}
      />
    );
  }

  if (state === 'no-records') {
    return (
      <NoDataState
        title="No financial reports yet"
        hint={`${who} is in the graph, but no financial reports have been ingested for it. Run financial research to pull its filings.`}
        message={detail}
        icon={Database}
        variant="neutral"
        action={onResearch ? { label: 'Run financial research', onClick: onResearch, icon: Sparkles } : undefined}
        asCard={asCard}
        cardTitle={cardTitle}
        cardSubtitle={cardSubtitle}
        minHeight={minHeight}
      />
    );
  }

  // not-ingested (404) — the backend can't tell "unknown company" apart from
  // "known company, nothing ingested", so the copy covers both honestly.
  const isUS = country?.toUpperCase() === 'US';
  const hint = isUS
    ? `${who}'s financial data will appear once the research pipeline completes — this usually takes 2–5 minutes. If it's been longer, re-run research.`
    : `No financial filings have been ingested for ${who} yet. Run financial research to pull its Profit & Loss and Balance Sheet.`;

  return (
    <NoDataState
      title={isUS ? 'Financial data still processing' : 'Not researched yet'}
      hint={hint}
      message={detail}
      icon={Database}
      variant="neutral"
      action={onResearch ? { label: 'Run financial research', onClick: onResearch, icon: Sparkles } : undefined}
      asCard={asCard}
      cardTitle={cardTitle}
      cardSubtitle={cardSubtitle}
      minHeight={minHeight}
    />
  );
}
